import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Copy, Check, Menu, X, BookOpen, Zap, Shield, FileJson } from 'lucide-react';
import visionLiveLogo from '../assets/images/visionlive.png';

const BASE_URL = 'https://api.vltryon.com/api';

const SECTION_IDS = [
  'intro',
  'base-url',
  'authentication',
  'vto-init',
  'vto-check',
  'vto-catalog',
  'rate-limits',
  'widget',
];

// -----------------------------------------------------------------------------
// Copyable code block (Whop/Stoplight-style)
// -----------------------------------------------------------------------------
const CodeBlock = ({ label, code, language = 'text' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="group rounded-xl overflow-hidden border border-white/10 bg-[#0a0a0f]">
      <div className="flex items-center justify-between px-4 py-2 bg-white/[0.04] border-b border-white/10">
        <span className="text-xs font-medium text-white/50 uppercase tracking-wider">{label || language}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-white/50 hover:text-white hover:bg-white/10 transition-colors"
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5 text-emerald-400" />
              Copied
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" />
              Copy
            </>
          )}
        </button>
      </div>
      <pre className="p-4 overflow-x-auto text-sm font-mono text-blue-200/90 leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Method badge (GET / POST)
// -----------------------------------------------------------------------------
const MethodBadge = ({ method }) => {
  const isGet = method === 'GET';
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
        isGet ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'
      }`}
    >
      {method}
    </span>
  );
};

// -----------------------------------------------------------------------------
// Section heading with anchor
// -----------------------------------------------------------------------------
const SectionHeading = ({ tag = 'h2', children, className = '' }) => {
  const Tag = tag;
  return <Tag className={`scroll-mt-28 font-semibold text-white ${className}`}>{children}</Tag>;
};

// -----------------------------------------------------------------------------
// Nav link
// -----------------------------------------------------------------------------
const NavItem = ({ href, active, children, indent = false }) => (
  <a
    href={href}
    className={`block py-2 text-sm transition-colors rounded-lg px-3 -mx-1 ${
      indent ? 'pl-6' : ''
    } ${
      active
        ? 'text-[#3B82F6] bg-blue-500/10 font-medium'
        : 'text-white/50 hover:text-white/80 hover:bg-white/5'
    }`}
  >
    {children}
  </a>
);

// -----------------------------------------------------------------------------
// Main API Docs Page
// -----------------------------------------------------------------------------
const ApiDocsPage = ({ onBack }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeId, setActiveId] = useState('intro');
  const mainRef = useRef(null);

  // Scroll spy: highlight nav item for section in view
  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          const id = e.target.id;
          if (SECTION_IDS.includes(id)) setActiveId(id);
        }
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );

    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white font-sans antialiased">
      {/* Top bar */}
      <header className="sticky top-0 z-40 flex items-center justify-between gap-4 px-4 py-3 bg-[#0d0d0d]/95 backdrop-blur-md border-b border-white/5 md:px-6">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onBack}
            className="flex-shrink-0 p-2 rounded-lg hover:bg-white/10 transition-colors text-white/60 hover:text-white"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <a href="/" className="flex items-center gap-2 flex-shrink-0">
            <img src={visionLiveLogo} alt="VisionLive" className="h-8 w-auto max-w-[160px] object-contain" />
          </a>
          <span className="hidden sm:inline text-white/30 text-sm flex-shrink-0">/</span>
          <span className="hidden sm:inline text-white/60 text-sm font-medium truncate">API Reference</span>
        </div>
        <button
          onClick={() => setSidebarOpen((o) => !o)}
          className="flex-shrink-0 md:hidden p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white"
          aria-label="Toggle menu"
        >
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </header>

      <div className="flex">
        {/* Sidebar (Whop/Stoplight-style nav) */}
        <aside
          className={`
            fixed inset-y-0 left-0 z-30 w-64 flex flex-col pt-[57px] bg-[#0a0a0f] border-r border-white/5
            transform transition-transform duration-200 ease-out md:relative md:translate-x-0 md:pt-6 md:top-0
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          `}
        >
          <div className="flex-1 overflow-y-auto py-4 px-3">
            <nav className="space-y-0.5" onClick={closeSidebar}>
              <div className="px-3 py-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-white/40">
                  Introduction
                </span>
              </div>
              <NavItem href="#intro" active={activeId === 'intro'}>
                Getting started
              </NavItem>
              <NavItem href="#base-url" active={activeId === 'base-url'}>
                Base URL
              </NavItem>
              <NavItem href="#authentication" active={activeId === 'authentication'}>
                Authentication
              </NavItem>

              <div className="px-3 py-2 mt-4">
                <span className="text-xs font-semibold uppercase tracking-wider text-white/40">
                  Endpoints
                </span>
              </div>
              <NavItem href="#vto-init" active={activeId === 'vto-init'} indent>
                POST /vto/init
              </NavItem>
              <NavItem href="#vto-check" active={activeId === 'vto-check'} indent>
                GET /vto/check
              </NavItem>
              <NavItem href="#vto-catalog" active={activeId === 'vto-catalog'} indent>
                GET /vto/catalog
              </NavItem>

              <div className="px-3 py-2 mt-4">
                <span className="text-xs font-semibold uppercase tracking-wider text-white/40">
                  More
                </span>
              </div>
              <NavItem href="#rate-limits" active={activeId === 'rate-limits'}>
                Rate limits
              </NavItem>
              <NavItem href="#widget" active={activeId === 'widget'}>
                Widget integration
              </NavItem>
            </nav>
          </div>
        </aside>

        {/* Overlay when sidebar open on mobile */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeSidebar}
              className="fixed inset-0 z-20 bg-black/60 md:hidden"
            />
          )}
        </AnimatePresence>

        {/* Main content */}
        <main
          ref={mainRef}
          className="flex-1 min-w-0 max-w-3xl mx-auto px-4 py-10 md:px-8 md:py-12 lg:px-12"
        >
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-12"
          >
            {/* Intro */}
            <section id="intro" className="space-y-4 scroll-mt-28">
              <div className="flex items-center gap-2 text-white/40 text-sm">
                <BookOpen className="w-4 h-4" />
                <span>Introduction</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
                Getting started
              </h1>
              <p className="text-white/60 text-base leading-relaxed max-w-2xl">
                Integrate the VisionLive virtual try-on widget into your e-commerce store via REST API.
                Get your API key and manage allowed domains in the{' '}
                <a href="/dashboard" className="text-[#3B82F6] hover:underline">
                  dashboard
                </a>
                .
              </p>
            </section>

            {/* Base URL */}
            <section id="base-url" className="space-y-4 scroll-mt-28">
              <SectionHeading tag="h2" className="text-lg mb-2">
                Base URL
              </SectionHeading>
              <p className="text-white/60 text-sm leading-relaxed">
                All API requests use the following base URL:
              </p>
              <CodeBlock label="Base URL" code={BASE_URL} />
            </section>

            {/* Authentication */}
            <section id="authentication" className="space-y-4 scroll-mt-28">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-white/40" />
                <SectionHeading tag="h2" className="text-lg">
                  Authentication
                </SectionHeading>
              </div>
              <p className="text-white/60 text-sm leading-relaxed">
                Widget endpoints (<strong className="text-white/80">init</strong>,{' '}
                <strong className="text-white/80">check</strong>,{' '}
                <strong className="text-white/80">catalog</strong>) require:
              </p>
              <ul className="list-disc pl-5 space-y-2 text-white/60 text-sm">
                <li>
                  <strong className="text-white/80">API key</strong> in the{' '}
                  <code className="px-1.5 py-0.5 rounded bg-white/10 text-blue-300 text-xs">
                    Authorization
                  </code>{' '}
                  header: <code className="px-1.5 py-0.5 rounded bg-white/10 text-blue-300 text-xs">Bearer YOUR_API_KEY</code>
                </li>
                <li>
                  <strong className="text-white/80">Origin</strong> header matching a domain you&apos;ve whitelisted.
                  Non-whitelisted origins (except <code className="px-1.5 py-0.5 rounded bg-white/10 text-blue-300 text-xs">localhost</code>) are rejected.
                </li>
              </ul>
              <CodeBlock
                label="cURL"
                code={`curl -X POST "${BASE_URL}/vto/init" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -H "Origin: https://your-store.com" \\
  -d '{"sku": "PRODUCT_SKU"}'`}
              />
            </section>

            {/* POST /vto/init */}
            <section id="vto-init" className="space-y-4 scroll-mt-28">
              <div className="flex flex-wrap items-center gap-2">
                <MethodBadge method="POST" />
                <code className="text-sm font-mono text-white/80 bg-white/5 px-2 py-1 rounded">/vto/init</code>
              </div>
              <h2 className="text-lg font-medium text-white/90">
                Initialize widget
              </h2>
              <p className="text-white/60 text-sm leading-relaxed">
                Initialize the widget with a single product by SKU. Returns product config and a short-lived session token.
              </p>
              <div className="space-y-3">
                <p className="text-white/70 text-sm font-medium">Request</p>
                <CodeBlock
                  label="JavaScript"
                  code={`fetch('${BASE_URL}/vto/init', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json',
    'Origin': 'https://your-store.com'
  },
  body: JSON.stringify({ sku: 'PRODUCT_SKU' })
});`}
                />
              </div>
              <div className="space-y-3">
                <p className="text-white/70 text-sm font-medium">Response (2D product)</p>
                <CodeBlock
                  label="JSON"
                  code={`{
  "product": {
    "id": "...",
    "sku": "RB-3025",
    "name": "Classic Aviator",
    "assetType": "2d",
    "frontImageUrl": "https://...",
    "handleImageUrl": "https://...",
    "thumbnailUrl": "https://...",
    "templeConfig": { "length": 3, "hingeOffset": 0.95, "yOffset": 0.02, "handleHeight": 2 },
    "recommendedFaceShapes": []
  },
  "features": { "recording": true, "compare": true, "sharing": true },
  "sessionToken": "...",
  "expiresAt": "2026-01-24T12:00:00.000Z"
}`}
                />
              </div>
              <p className="text-white/50 text-sm">
                For <strong className="text-white/70">3D</strong> products, <code className="px-1 py-0.5 rounded bg-white/10 text-blue-300 text-xs">assetType</code> is{' '}
                <code className="px-1 py-0.5 rounded bg-white/10 text-blue-300 text-xs">3d</code> and{' '}
                <code className="px-1 py-0.5 rounded bg-white/10 text-blue-300 text-xs">modelUrl</code> is provided.
              </p>
              <p className="text-white/50 text-sm">
                <strong className="text-white/70">Errors:</strong>{' '}
                <span className="text-amber-400/90">400</span> SKU required ·{' '}
                <span className="text-amber-400/90">401</span> Missing/invalid API key ·{' '}
                <span className="text-amber-400/90">403</span> Origin not allowed ·{' '}
                <span className="text-amber-400/90">404</span> Product not found
              </p>
            </section>

            {/* GET /vto/check */}
            <section id="vto-check" className="space-y-4 scroll-mt-28">
              <div className="flex flex-wrap items-center gap-2">
                <MethodBadge method="GET" />
                <code className="text-sm font-mono text-white/80 bg-white/5 px-2 py-1 rounded">/vto/check</code>
              </div>
              <h2 className="text-lg font-medium text-white/90">
                Check product exists
              </h2>
              <p className="text-white/60 text-sm leading-relaxed">
                Check whether an active product exists for the given SKU. Use this to conditionally show a &quot;Try on&quot; button.
              </p>
              <CodeBlock
                label="Request"
                code={`GET ${BASE_URL}/vto/check?sku=PRODUCT_SKU
Authorization: Bearer YOUR_API_KEY
Origin: https://your-store.com`}
              />
              <p className="text-white/70 text-sm font-medium">Response (exists)</p>
              <CodeBlock label="JSON" code='{ "exists": true }' />
              <p className="text-white/70 text-sm font-medium">Response (not found)</p>
              <CodeBlock label="404" code='{ "error": "Product not found", "exists": false }' />
            </section>

            {/* GET /vto/catalog */}
            <section id="vto-catalog" className="space-y-4 scroll-mt-28">
              <div className="flex flex-wrap items-center gap-2">
                <MethodBadge method="GET" />
                <code className="text-sm font-mono text-white/80 bg-white/5 px-2 py-1 rounded">/vto/catalog</code>
              </div>
              <h2 className="text-lg font-medium text-white/90">
                List products
              </h2>
              <p className="text-white/60 text-sm leading-relaxed">
                Fetch all active products for your API key. Use for catalog-style try-on UIs.
              </p>
              <CodeBlock
                label="Request"
                code={`GET ${BASE_URL}/vto/catalog
Authorization: Bearer YOUR_API_KEY
Origin: https://your-store.com`}
              />
              <CodeBlock
                label="Response"
                code={`{
  "products": [
    {
      "id": "...",
      "sku": "RB-3025",
      "name": "Classic Aviator",
      "assetType": "2d",
      "frontImageUrl": "https://...",
      "handleImageUrl": "https://...",
      "thumbnailUrl": "https://...",
      "templeConfig": { ... },
      "recommendedFaceShapes": []
    }
  ],
  "features": { "recording": true, "compare": true, "sharing": true }
}`}
              />
            </section>

            {/* Rate limits */}
            <section id="rate-limits" className="space-y-4 scroll-mt-28">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-white/40" />
                <SectionHeading tag="h2" className="text-lg">
                  Rate limits
                </SectionHeading>
              </div>
              <p className="text-white/60 text-sm leading-relaxed">
                Widget endpoints are rate limited to <strong className="text-white/80">100 requests per minute</strong> per API key.
                Exceeding the limit returns <span className="text-amber-400/90">429</span>.
              </p>
            </section>

            {/* Widget integration */}
            <section id="widget" className="space-y-4 scroll-mt-28">
              <div className="flex items-center gap-2">
                <FileJson className="w-4 h-4 text-white/40" />
                <SectionHeading tag="h2" className="text-lg">
                  Widget integration
                </SectionHeading>
              </div>
              <p className="text-white/60 text-sm leading-relaxed">
                The simplest way to add try-on is the VisionLive widget script. See the{' '}
                <a href="/dashboard" className="text-[#3B82F6] hover:underline">dashboard</a> Implementation guide for
                embed code, or use the API above for custom integrations.
              </p>
            </section>

            {/* Footer */}
            <div className="pt-12 mt-12 border-t border-white/10">
              <p className="text-white/40 text-sm">
                © {new Date().getFullYear()} VisionLive. All rights reserved.
              </p>
            </div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default ApiDocsPage;
